import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { ASSETS, formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (user) {
    if (user.role === "staff") return <Navigate to="/staff" replace />;
    return <Navigate to="/admin" replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      if (u.role === "staff") navigate("/staff");
      else navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-[var(--trio-bg)] px-6 py-12"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img
            src={ASSETS.logo}
            alt="Trio"
            className="h-14 w-auto mx-auto mb-6"
          />
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-2">
            Owner Login
          </div>
          <h1 className="font-display uppercase text-4xl tracking-tight">
            Sign In to Admin
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-8 space-y-5"
        >
          <div>
            <Label htmlFor="login-email" className="text-xs tracking-widest uppercase">
              Email
            </Label>
            <Input
              id="login-email"
              type="email"
              required
              data-testid="admin-login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
            />
          </div>
          <div>
            <Label htmlFor="login-password" className="text-xs tracking-widest uppercase">
              Password
            </Label>
            <div className="relative mt-2">
              <Input
                id="login-password"
                type={showPassword ? "text" : "password"}
                required
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                data-testid="admin-login-toggle-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--trio-muted)] hover:text-[var(--trio-text)] p-1"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-1 text-right">
              <Link
                to="/forgot-password"
                data-testid="admin-forgot-link"
                className="text-[10px] tracking-widest uppercase text-[var(--trio-muted)] hover:text-[var(--trio-primary)]"
              >
                Forgot password?
              </Link>
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-3 text-sm tracking-widest uppercase disabled:opacity-60 transition-colors font-bold"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            Sign In
          </button>
        </form>

        <a
          href="/"
          className="block text-center mt-6 text-xs uppercase tracking-widest text-[var(--trio-muted)] hover:text-[var(--trio-primary)]"
          data-testid="admin-back-home"
        >
          ← Back to homepage
        </a>
        <a
          href="/staff/login"
          className="block text-center mt-3 text-xs uppercase tracking-widest text-[var(--trio-muted)] hover:text-[var(--trio-primary)]"
          data-testid="admin-to-staff-login"
        >
          Staff member? Sign in here
        </a>
      </div>
    </div>
  );
}
